import Isopleth from "./voronoi/Isopleth.js";

import RateCap           from "../utils/RateCap.js";

import Transform from "./Transform.js";

import global from "../global.js";

import {get_translation} from "svg-utils";

const log = global.log.getLogger("f_mtac.scene.voronoi");

const d3 = global.d3;
const color = d3.scaleSequential(n => `rgba${d3.interpolateSpectral(n).slice(3, -1)},${n === 1 ? 0 : 0.6})`).domain([255, 0]);

const Pole = function(x, y, v, r) {
    Object.defineProperty(this, "x", {
        set: (new_x) => {
            x = new_x;
        },
        get: () => x
    });
    Object.defineProperty(this, "y", {
        set: (new_y) => {
            y = new_y;
        },
        get: () => y
    });
    Object.defineProperty(this, "v", {
        set: (new_v) => {
            v = new_v;
        },
        get: () => v
    });
    Object.defineProperty(this, "r", {
        set: (new_r) => {
            r = new_r;
        },
        get: () => r
    });
};

const Poles = function() {
    var P = [];
    this.modified = Date.now();
    this.add = ({x, y, v, r}) => {
        let p = new Pole(x, y, v, r);
        P.push(p);
        return p;
    };
    this.get = (n) => P[n];
    this.last = () => P[P.length - 1];
    this.reset = () => {
        P = [];
    };
    Object.defineProperty(this, "centers", {
        set: () => {},
        get: () => P.map(p => [p.x, p.y])
    });
    Object.defineProperty(this, "radius", {
        set: () => {},
        get: () => P.map(p => p.r)
    });
    Object.defineProperty(this, "values", {
        set: () => {},
        get: () => P.map(p => p.v)
    });
};

const Voronoi = function(view, svg, objects, background_image) {
    this.voronoi_layer = svg.layer_create("voronoi");
    this.voronoi = this.voronoi_layer.append("g").attr("class", "voronoi");

    const poles = new Poles();
    var radius = 35;
    var bandwidth = 10;
    var height, width;

    const canvas = d3.create("xhtml:canvas");
    canvas.attr("x", 0).attr("y", 0);
    canvas.style("position", "absolute");

    const context = canvas.node().getContext("2d");

    view.container.append(canvas.node());

    Object.defineProperty(this, "radius", {
        set: (new_radius) => {
            radius = new_radius;
        },
        get: () => radius
    });

    Object.defineProperty(this, "bandwidth", {
        set: (new_bandwidth) => {
            bandwidth = new_bandwidth;
        },
        get: () => bandwidth
    });

    let node_on_creating_layer_special = async (handle) => {
        var object = objects.get_by_node("special", handle);
        if (object === undefined) {
            return;
        }
        log.error("created a new node", object);
        this.reload();
        this.draw();
    };

    let node_on_deleting_layer_special = async (handle) => {
        var object = objects.get_by_node("special", handle);
        if (object === undefined) {
            return;
        }
        log.error("deleted a node", object);
        this.reload();
        this.draw();
    };

    objects.layers.special.on("creating", node_on_creating_layer_special);
    objects.layers.special.on("deleting", node_on_deleting_layer_special);

    this.alpha = 0.8;
    this.composition = "xor";
    this.scale = 0.1;

    this.init = () => {
        width = background_image.width;
        height = background_image.height;
        canvas.attr("width", width).attr("height", height);
    };

    this.deinit = () => {
        width = undefined;
        height = undefined;
        canvas.attr("width", null).attr("height", null);
    };

    const draw = async () => {
        var centers = poles.centers;
        if (centers.length === 0) {
            // nothing to draw
            return;
        }
        var values = poles.values;
        var borderradius = poles.radius;
        var width = canvas.node().width, height = canvas.node().height;
        var isopleth = Isopleth(centers, width, height, this.scale);
        context.clearRect(0, 0, width, height);
        context.globalAlpha = this.alpha;
        context.globalCompositeOperation = this.composition;
        var path = d3.geoPath().context(context);
        context.strokeStyle = "rgba(0,0,0,0)";
        const levels = isopleth(values, bandwidth, borderradius);
        for (const level of levels) {
            if (level.value < 0) level.value = 0;
            context.globalAlpha = this.alpha;
            context.globalCompositeOperation = this.composition;
            context.beginPath();
            path(level);
            context.fillStyle = color(level.value);
            if (level.value % 25 === 0) {
                context.lineWidth = level.value % 100 ? 0.5 : 2;
                context.stroke();
            }
            context.fill();
        }
        // hidding poles
        /*
        for (let i = 0; i < values.length; i++) {
            context.globalAlpha = this.alpha;
            context.globalCompositeOperation = this.composition;
            context.beginPath();
            path({ type: "Point", coordinates: centers[i] });
            context.fillStyle = color(values[i]);
            context.fill();
            context.stroke();
        }
        */
        svg.layer_insert("voronoi", "grid");
    };

    const draw_cap = new RateCap(() => {
        draw();
    }, 33);

    this.draw = () => {
        draw_cap.poll();
    };

    this.reset = () => {
        context.clearRect(0, 0, width, height);
        poles.reset();
    };

    this.add = p => {
        if (!("radius" in p)) {
            p.r = radius;
        }
        poles.add(p);
    };

    this.reload = () => {
        poles.reset();
        d3.selectAll("#special .object").each(function() {
            let {x, y} = get_translation(this.getAttribute("transform"));
            let v = parseInt(this.getAttribute("data-voronoi-intensity"));
            poles.add({x, y, v, r: radius});
        });
    };

    this.hide = () => {
        canvas.style("display", "none");
    };

    this.show = () => {
        canvas.style("display", null);
    };

    this.transform = new Transform({x: 0, y: 0, k: 1});
    this.transform.on_change(({x, y, k}) => {
        this.voronoi.attr("transform", "translate(" + x + "," + y + ") scale(" + k + ")");
        canvas.style("transform", `translate(${x}px, ${y}px) scale(${k})`);
    });
    canvas.style("transform-origin", `-${view.padding.left}px -${view.padding.top}px`);
    canvas.style("z-index", 2);
};

export default Voronoi;
